/**
 * Role-based access control — Functional Specification §3 permission matrix, plus
 * FR-ADM-07 scoped system access. Pure functions shared by API guards and UI visibility.
 */
import type { AdminRole } from './enums';

export const RESOURCES = [
  'systems', // systems, modules, roles, documents
  'credentials', // reveal / manage test credentials
  'testCases',
  'sessions',
  'invitations',
  'feedback',
  'executions',
  'reports',
  'signoff',
  'settings', // tenant settings & integrations
  'users', // users & access
  'audit',
  'ai',
] as const;
export type Resource = (typeof RESOURCES)[number];

export const ACTIONS = ['read', 'create', 'update', 'delete', 'approve', 'sign', 'reveal', 'triage'] as const;
export type Action = (typeof ACTIONS)[number];

type Matrix = Record<AdminRole, Partial<Record<Resource, readonly Action[]>>>;

const CRUD = ['read', 'create', 'update', 'delete'] as const;

export const PERMISSIONS: Matrix = {
  super_admin: {
    systems: CRUD,
    credentials: ['read', 'create', 'update', 'delete', 'reveal'],
    testCases: [...CRUD, 'approve'],
    sessions: CRUD,
    invitations: CRUD,
    feedback: [...CRUD, 'triage'],
    executions: ['read'],
    reports: CRUD,
    signoff: ['read', 'create'],
    settings: CRUD,
    users: CRUD,
    audit: ['read'],
    ai: ['read', 'create'],
  },
  manager: {
    systems: CRUD,
    credentials: ['read', 'create', 'update', 'delete', 'reveal'],
    testCases: [...CRUD, 'approve'],
    sessions: CRUD,
    invitations: CRUD,
    feedback: [...CRUD, 'triage'],
    executions: ['read'],
    reports: CRUD,
    signoff: ['read', 'create'],
    settings: ['read'],
    audit: ['read'],
    ai: ['read', 'create'],
  },
  author: {
    systems: ['read'],
    testCases: CRUD,
    sessions: ['read'],
    feedback: ['read'],
    executions: ['read'],
    reports: ['read'],
    ai: ['read', 'create'],
  },
  reviewer: {
    systems: ['read'],
    testCases: ['read'],
    sessions: ['read'],
    invitations: ['read'],
    feedback: ['read', 'update', 'triage'],
    executions: ['read'],
    reports: ['read'],
    ai: ['read'],
  },
  sponsor: {
    systems: ['read'],
    testCases: ['read'],
    sessions: ['read'],
    feedback: ['read'],
    executions: ['read'],
    reports: ['read'],
    signoff: ['read', 'sign'],
    ai: ['read'],
  },
};

export function can(role: AdminRole, resource: Resource, action: Action): boolean {
  return PERMISSIONS[role][resource]?.includes(action) ?? false;
}

export function isGlobalAdmin(role: AdminRole): boolean {
  return role === 'super_admin';
}

export interface ScopedPrincipal {
  role: AdminRole;
  systemGrants: readonly string[];
}

/** FR-ADM-07 / business rule 12: non-global admins only ever see systems they were granted. */
export function canAccessSystem(principal: ScopedPrincipal, systemId: string): boolean {
  return isGlobalAdmin(principal.role) || principal.systemGrants.includes(systemId);
}

/** Systems visible to the principal; `null` means "all systems" (global admin). */
export function visibleSystemIds(principal: ScopedPrincipal): readonly string[] | null {
  return isGlobalAdmin(principal.role) ? null : principal.systemGrants;
}

export const ROLE_LABELS: Record<AdminRole, string> = {
  super_admin: 'Global admin',
  manager: 'Admin',
  author: 'Author',
  reviewer: 'Reviewer',
  sponsor: 'Sponsor',
};
